import { useState } from 'react'

import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { SinDatos } from '@/shared/ui/Estados/SinDatos'
import { Paginacion } from '@/shared/ui/Paginacion/Paginacion'
import { ThOrdenable, type Orden } from '@/shared/ui/Tabla/OrdenTabla'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import { pesos } from '@/shared/utilidades/formato'

import { useInventario, type CampoInventario, type FiltroInventario } from './useComercial'
import estilos from './Comercial.module.css'

/**
 * Las unidades se escriben enteras cuando lo son.
 *
 * World Office las guarda con seis decimales —«9087.000000»— porque hay cosas
 * que se venden por metro. Un tornillo con seis ceros detrás no se lee; un
 * cable de 12,5 metros sí necesita su coma.
 */
function unidades(cuantas: number | null): string {
  if (cuantas === null) return '—'
  return Number.isInteger(cuantas)
    ? cuantas.toLocaleString('es-CO')
    : cuantas.toLocaleString('es-CO', { maximumFractionDigits: 2 })
}

/**
 * El inventario de World Office.
 *
 * Hasta ahora los 2.516 productos solo aparecían dentro del buscador del
 * cotizador: para saber si algo existía o cuánto valía había que abrir una
 * cotización. Aquí se consultan de frente.
 *
 * Se ordenan por cuántas veces se han cotizado, que es lo que separa lo que
 * de verdad se vende de lo que solo ocupa espacio en el catálogo.
 */
export function PaginaInventario() {
  const [termino, setTermino] = useState('')
  const [filtro, setFiltro] = useState<FiltroInventario>('todos')
  // Lo más cotizado primero: separa lo que de verdad se vende de lo que solo
  // ocupa catálogo.
  const [orden, setOrden] = useState<Orden<CampoInventario>>({
    campo: 'veces_cotizado',
    ascendente: false,
  })
  const [pagina, setPagina] = useState(0)

  const {
    filas: productos,
    total,
    cargando,
    error,
    porPagina,
  } = useInventario(termino, filtro, orden, pagina)

  /**
   * Filtrar vuelve a la primera página.
   *
   * Va junto al cambio del filtro y no en un efecto posterior: con el efecto,
   * entre el filtro nuevo y el reinicio se cuela una consulta que pide, por
   * ejemplo, la página 51 de un resultado que ahora tiene 4 — y el servidor
   * responde 416, rango imposible.
   */
  function filtrar(cambio: () => void) {
    cambio()
    setPagina(0)
  }

  /** Cambiar de columna también vuelve al principio. */
  function ordenar(nuevo: Orden<CampoInventario>) {
    setOrden(nuevo)
    setPagina(0)
  }

  return (
    <Tarjeta
      subtitulo="Los productos de World Office, con lo que hay en bodega. Los precios y las existencias se administran allá."
      sinRelleno
      pie={
        <Paginacion pagina={pagina} porPagina={porPagina} total={total} onCambiar={setPagina} />
      }
      acciones={
        <div className={estilos.filtros}>
          <input
            className={estilos.buscador}
            type="search"
            placeholder="Nombre o código…"
            value={termino}
            onChange={(e) => filtrar(() => setTermino(e.target.value))}
            aria-label="Buscar producto"
          />

          <select
            className={estilos.selector}
            value={filtro}
            onChange={(e) => filtrar(() => setFiltro(e.target.value as FiltroInventario))}
            aria-label="Filtrar"
          >
            <option value="todos">Todos</option>
            <option value="con-existencias">Hay en bodega</option>
            <option value="agotados">Agotados</option>
            <option value="usados">Ya cotizados</option>
            <option value="con-precio">Con precio</option>
            <option value="sin-precio">Precio a convenir</option>
          </select>
        </div>
      }
    >
      {cargando ? (
        <Cargando mensaje="VERIFICANDO EXISTENCIAS BODEGA..." />
      ) : error ? (
        <div style={{ padding: 'var(--e-5)' }}>
          <Aviso tipo="error" titulo="No se pudo cargar">
            {error}
          </Aviso>
        </div>
      ) : productos.length === 0 ? (
        <SinDatos
          titulo="Ningún producto con ese nombre"
          mensaje="Si no aparece, hay que crearlo en World Office."
        />
      ) : (
        <div className={estilos.desplazable}>
          <table className={estilos.tabla}>
            <thead>
              <tr>
                <th style={{ width: 108 }}>Código</th>

                {/* Por el nombre, que es lo que se está leyendo. Ordenar por
                    la descripción cruda ordenaría por el código que World
                    Office le antepone, y el resultado parece aleatorio. */}
                <ThOrdenable campo="nombre" orden={orden} onCambiar={ordenar}>
                  Producto
                </ThOrdenable>

                <ThOrdenable
                  campo="precio_sin_iva"
                  orden={orden}
                  onCambiar={ordenar}
                  derecha
                  ancho={126}
                  primeroDescendente
                >
                  Sin IVA
                </ThOrdenable>

                <th className={estilos.derecha} style={{ width: 126 }}>
                  Con IVA
                </th>
                <th className={estilos.derecha} style={{ width: 74 }}>
                  IVA
                </th>

                <ThOrdenable
                  campo="veces_cotizado"
                  orden={orden}
                  onCambiar={ordenar}
                  derecha
                  ancho={104}
                  primeroDescendente
                >
                  Cotizado
                </ThOrdenable>

                {/* Al final y no al principio: se llega aquí buscando un
                    producto por su nombre, y las existencias son lo que se
                    mira DESPUÉS de haberlo encontrado —«¿lo tengo o toca
                    comprarlo?»—. Ordenable de mayor a menor para poder pedir
                    la otra pregunta: qué falta. */}
                <ThOrdenable
                  campo="existencias"
                  orden={orden}
                  onCambiar={ordenar}
                  derecha
                  ancho={112}
                  primeroDescendente
                >
                  Hay
                </ThOrdenable>
              </tr>
            </thead>

            <tbody>
              {productos.map((producto) => (
                <tr key={producto.id_inventario}>
                  <td className={producto.codigo ? estilos.codigo : estilos.tenue}>
                    {producto.codigo ?? '—'}
                  </td>

                  <td className={estilos.nombre}>{producto.nombre}</td>

                  {producto.precio_a_convenir ? (
                    <td className={estilos.tenue} colSpan={2} style={{ textAlign: 'right' }}>
                      Precio a convenir
                    </td>
                  ) : (
                    <>
                      <td className={`${estilos.derecha} ${estilos.cifra}`}>
                        {pesos(producto.precio_sin_iva)}
                      </td>
                      <td className={`${estilos.derecha} ${estilos.cifra}`}>
                        {pesos(producto.precio_con_iva)}
                      </td>
                    </>
                  )}

                  <td className={`${estilos.derecha} ${estilos.apagado}`}>
                    {producto.iva ? `${Math.round(producto.iva * 100)} %` : '—'}
                  </td>

                  <td
                    className={`${estilos.derecha} ${
                      producto.veces_cotizado ? estilos.cifra : estilos.tenue
                    }`}
                  >
                    {producto.veces_cotizado || 'Nunca'}
                  </td>

                  {/* Cuatro estados, y los cuatro dicen algo distinto:
                      · un número      — hay tantas
                      · «Agotado»      — se guarda en bodega y no queda: hay
                                         que reponerlo, así que va en ámbar
                      · un negativo    — World Office está descuadrado, salió
                                         más de lo que entró. En rojo, porque
                                         es un error que alguien debe corregir
                                         allá y no aquí
                      · «—»            — no es una cosa que se inventaríe: un
                                         seguro, unos gastos legales. Escribir
                                         «0» aquí sería inventarse un faltante */}
                  <td className={estilos.derecha}>
                    {!producto.se_inventaria ? (
                      <span className={estilos.tenue}>—</span>
                    ) : producto.existencias! < 0 ? (
                      <span className={estilos.descuadre}>{unidades(producto.existencias)}</span>
                    ) : producto.existencias === 0 ? (
                      <span className={estilos.agotado}>Agotado</span>
                    ) : (
                      <span className={estilos.cifra}>{unidades(producto.existencias)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Tarjeta>
  )
}
